import api from 'service/api'

export type CategoryMember = {
  userId: number
  fullName: string
  communityId: number
  categoryId: number
  memberRoleId: number
}

export type Category = {
  id: number
  communityId: number
  categoryName: string
  description: string
  colorId: number
  iconId: number
  forAllCommunityUsers: boolean
  categoryManagers: CategoryMember[]
  approvingManagers: CategoryMember[]
  viewers: CategoryMember[]
  viewerTags?: string[]
}

export const fetchCategories = async (baseUrl: any, authToken: any, path: any): Promise<Category[]> => {
  const data = await api.getCategories(baseUrl, authToken, path)
  return data
}

export const createCategory = async (baseUrl: any, authToken: any, body: any) => {
  return api.createCategory(baseUrl, authToken, body)
}

export const updateCategory = async (baseUrl: any, authToken: any, categoryId: number, body: any) => {
  return api.updateCategory(baseUrl, authToken, categoryId, body)
}

export const deleteCategory = async (baseUrl: any, authToken: any, categoryId: number) => {
  return api.deleteCategory(baseUrl, authToken, categoryId)
}

export const deleteProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
  return api.deleteProtocol(baseUrl, authToken, protocolId)
}

export const archiveProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
  return api.archiveProtocol(baseUrl, authToken, protocolId)
}

export const restoreProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
  return api.restoreProtocol(baseUrl, authToken, protocolId)
}

export const downloadProtocolFile = async (baseUrl: any, authToken: any, protocolId: number, fileId: number, fileName: string) => {
  return api.downloadProtocolFile(baseUrl, authToken, protocolId, fileId, fileName)
}

export const openProtocolFile = async (baseUrl: any, authToken: any, protocolId: number, fileId: number, fileName: string) => {
  return api.openProtocolFile(baseUrl, authToken, protocolId, fileId, fileName)
}

export type UserOption = {
  userId: number
  fullName: string
  tags?: string
}

export const fetchUserList = async (baseUrl: any, authToken: any): Promise<UserOption[]> => {
  return api.getProtocolUserList(baseUrl, authToken)
}

export const fetchUserNames = async (baseUrl: any, authToken: any): Promise<UserOption[]> => {
  return api.getProtocolUserNames(baseUrl, authToken)
}

export type MainManagerUser = {
  userId: number
  communityId: number
  fullName: string
  israeliId: number
  isMainManager: boolean
  protocolEmail: string
}

export const fetchMainManagers = async (baseUrl: any, authToken: any): Promise<MainManagerUser[]> => {
  return api.getMainManagers(baseUrl, authToken)
}

export type MainManagerEntry = {
  isMainManager: boolean
  userId: number
  protocolEmail: string
}

export const patchMainManagerWithEmail = async (baseUrl: any, authToken: any, body: MainManagerEntry[]) => {
  return api.patchMainManagerWithEmail(baseUrl, authToken, body)
}

export type ArchiveItem = {
  id: number
  title: string
  categoryName: string
  creatorName: string
  protocolDate: string
  version: string
}

export type ArchiveListResponse = {
  protocols: ArchiveItem[]
  totalCount: number
}

export type ProtocolItem = {
  id: number
  categoryId: number
  categoryName: string
  title: string
  protocolDate: string
  creatorName: string
  topics: string
  rejectReason: string
  protocolStatusId: number
  participantsCount: number
  filesCount: number
  viewsCount: number
  isRead: boolean
  isEditAll: boolean
  isEditStatus: boolean
  isDelete: boolean
  isArchive: boolean
}

export type ProtocolsListResponse = {
  protocols: ProtocolItem[]
  totalCount: number
}

export type ProtocolCountsByStatus = {
  totalCount: number
  draftCount: number
  pendingApprovalCount: number
  approvedCount: number
  rejectedCount: number
}

export const fetchProtocolCountsByStatus = async (baseUrl: any, authToken: any): Promise<ProtocolCountsByStatus> => {
  return api.fetchProtocolCountsByStatus(baseUrl, authToken)
}

export type ProtocolsListBody = {
  searchText?: string
  searchMode?: number
  categoryId?: number
  creatorName?: string
  fromDate?: string
  toDate?: string
  filterByStatus?: number
  sortProperty?: string
  sortDirection?: string
  pageNumber: number
  pageSize: number
}

export type ExchangeTokenResponse = {
  accessToken: string
  expiresInSeconds: number
}


export const fetchProtocols = async (
  baseUrl: any,
  authToken: any,
  body: ProtocolsListBody
): Promise<ProtocolsListResponse> => {
  return api.fetchProtocols(baseUrl, authToken, body)
}

export const fetchArchiveList = async (baseUrl: any, authToken: any, body: ProtocolsListBody) => {
  return api.getArchiveList(baseUrl, authToken, body)
}

export type CreateProtocolBody = {
  title: string
  categoryId: number
  protocolDate: string
  protocolStatusId: number
  participants: { userId: number; participantRole: string }[]
  topics: string
  decisions: string
  description: string
}

export const createProtocol = async (baseUrl: any, authToken: any, body: CreateProtocolBody) => {
  return api.createProtocol(baseUrl, authToken, body)
}

export type ProtocolFile = {
  id: number
  protocolId: number
  fileName: string
  fileFormat: string
  isMainFile: boolean
  uploadedDate: string
  blobPath: string
  fileSize: number
  downloadUrl: string
}

export type ProtocolViewData = {
  title: string
  version: number
  modifiedDate: string
  protocolDate: string
  creatorName: string
  categoryId: number
  categoryName: string
  participants: { participantName: string; participantRole: string }[]
  topics: string
  decisions: string
  description: string
  mainFile: ProtocolFile | null
  attachments: ProtocolFile[]
  // Populated once the for-view endpoint returns them; used to show the
  // rejection reason in the view when a protocol is rejected.
  protocolStatusId?: number
  rejectReason?: string
  // Approvers and their ProtocolApprovmentStatuses status (1=Waiting, 2=Approved, 3=Rejected).
  // Only populated by the API for Admin/Main Manager/Category Manager.
  approvers?: { approverName: string; statusId: number; statusName: string }[]
}

export const fetchProtocolForView = async (baseUrl: any, authToken: any, protocolId: number): Promise<ProtocolViewData> => {
  return api.getProtocolForView(baseUrl, authToken, protocolId)
}

export type ProtocolStatusHistoryItem = {
  id: number
  newStatusId: number
  originalStatusId: number
  editorName: string
  createdDate: string
  // Set by the API on a rejection row - the reason the approver gave.
  rejectReason?: string
}

export type ProtocolStatusHistoryResponse = {
  items: ProtocolStatusHistoryItem[]
  totalCount: number
}

export type ProtocolStatusHistoryQuery = {
  pageNumber: number
  pageSize: number
  fromDate?: string
  toDate?: string
}

export const fetchProtocolStatusHistory = async (
  baseUrl: any, authToken: any, protocolId: number, query: ProtocolStatusHistoryQuery
): Promise<ProtocolStatusHistoryResponse> => {
  return api.getProtocolStatusHistory(baseUrl, authToken, protocolId, query)
}

export type ProtocolEditParticipant = {
  userId: number
  participantName: string
  participantRole: string
}

export type ProtocolEditData = {
  title: string
  version: number
  modifiedDate: string
  protocolDate: string
  creatorName: string
  categoryId: number
  categoryName: string
  protocolStatusId: number
  participants: ProtocolEditParticipant[]
  topics: string
  decisions: string
  description: string
  mainFile: ProtocolFile | null
  attachments: ProtocolFile[]
  isPublic: boolean
  // True when at least one approver has approved - Category Manager can then publish.
  canPublish: boolean
}

export const fetchProtocolForEdit = async (baseUrl: any, authToken: any, protocolId: number): Promise<ProtocolEditData> => {
  return api.getProtocolForEdit(baseUrl, authToken, protocolId)
}

// Minimal data for the Approve/Reject screen (GET /protocols/{id}/for-edit-status).
export type ProtocolStatusEditData = {
  id: number
  title: string
  version: number
  modifiedDate: string
  protocolStatusId: number
  // Status previously selected by the current approver: 1 - Waiting, 2 - Approved, 3 - Rejected. Null if none.
  myApprovmentStatusId: number | null
  // True while the protocol is Pending Approval or Rejected.
  canApprove: boolean
}

export const fetchProtocolForEditStatus = async (baseUrl: any, authToken: any, protocolId: number): Promise<ProtocolStatusEditData> => {
  return api.getProtocolForEditStatus(baseUrl, authToken, protocolId)
}

export type UpdateProtocolBody = {
  title: string
  protocolDate: string
  newCategoryId: number
  protocolStatusId: number
  participants: { userId: number; participantRole: string }[]
  topics: string
  decisions: string
  description: string
  isPublic: boolean
}

export const updateProtocol = async (baseUrl: any, authToken: any, protocolId: number, body: UpdateProtocolBody) => {
  return api.updateProtocol(baseUrl, authToken, protocolId, body)
}

export type UpdateProtocolStatusBody = {
  protocolStatusId: number
  rejectReason: string
}

export const publishProtocol = async (baseUrl: any, authToken: any, protocolId: number) => {
  return api.publishProtocol(baseUrl, authToken, protocolId)
}

export const updateProtocolStatus = async (baseUrl: any, authToken: any, protocolId: number, body: UpdateProtocolStatusBody) => {
  return api.updateProtocolStatus(baseUrl, authToken, protocolId, body)
}

export type FileToUpload = { file: File; isMainFile: boolean }

export const uploadProtocolFiles = async (baseUrl: any, authToken: any, protocolId: number, files: FileToUpload[]) => {
  return api.uploadProtocolFiles(baseUrl, authToken, protocolId, files)
}

export const deleteProtocolFiles = async (baseUrl: any, authToken: any, protocolId: number, fileIds: number[], filesToUpload: FileToUpload[] = []) => {
  return api.updateProtocolFiles(baseUrl, authToken, protocolId, fileIds, filesToUpload)
}

export type CategoryAccess = {
  categoryId: number
  memberRoleId: number
}

export type UserPermissions = {
  isAdmin?: boolean
  isMainManager?: boolean
  allAvailableCategories?: CategoryAccess[]
}

export const fetchUsers = async (baseUrl: any, authToken: any): Promise<UserOption[]> => {
  return api.getProtocolUsers(baseUrl, authToken)
}

export const fetchUserPermissions = async (baseUrl: any, authToken: any): Promise<UserPermissions | null> => {
  return api.getProtocolUserPermissions(baseUrl, authToken)
}

export type TermsOfUse = {
  isAcceptedTerms: boolean
}

export const fetchTermsOfUse = async (baseUrl: any, authToken: any): Promise<TermsOfUse | null> => {
  return api.getProtocolTermsOfUse(baseUrl, authToken)
}

export const acceptTermsOfUse = async (baseUrl: any, authToken: any) => {
  return api.acceptProtocolTermsOfUse(baseUrl, authToken)
}
